import { ChevronRightIcon } from '@c/icons';
import { LogoWithoutText } from '@c/icons/logoWithoutText';
import type { ChatDocument, ChatMessageDocument } from '@models/chat';
import Link from 'next/link';
import MessageTimestamp from './MessageTimestamp';

interface RequestMessageProps {
  message: ChatMessageDocument;
  chat: ChatDocument;
  label: string;
  reason: string | null;
  bodyText: string;
  isLoading?: boolean;
  orderNumFallback?: string | null;
}

export default function RequestMessage({
  message,
  chat,
  label,
  reason,
  bodyText,
  isLoading = false,
  orderNumFallback = null,
}: RequestMessageProps) {
  const displayOrderNum = chat.order_num?.toString() ?? orderNumFallback;
  const orderHref = chat.order_id
    ? `/dashboard/purchases/${chat.order_id}`
    : null;

  return (
    <div className="flex max-w-[30rem] items-end gap-[1.2rem] self-start py-[1.6rem] text-[1.6rem] font-medium sm:max-w-[50rem]">
      <div className="hidden h-[4rem] w-[4rem] shrink-0 items-center justify-center rounded-full bg-black p-3 sm:flex">
        <LogoWithoutText className="h-full w-full" fill="white" />
      </div>

      <div className="flex flex-col items-start gap-[0.8rem]">
        <MessageTimestamp
          timestamp={message.created_at ?? 0}
          isOutbound={false}
        />

        <div className="w-[34rem] space-y-6 rounded-br-[1.5rem] rounded-tl-[1.5rem] rounded-tr-[1.5rem] bg-[#444] p-6 text-zinc-100 shadow-sm sm:w-[38rem]">
          <div className="flex items-center justify-between gap-4">
            <div className="rounded-full bg-zinc-600 px-5 py-2 text-[1.4rem] font-semibold text-zinc-100">
              {label}
            </div>
            {displayOrderNum &&
              (orderHref ? (
                <Link
                  href={orderHref}
                  className="flex items-center gap-1 text-[1.5rem] font-semibold text-zinc-100 transition-colors hover:text-white"
                >
                  Order #{displayOrderNum}
                  <ChevronRightIcon className="h-6 w-6" />
                </Link>
              ) : (
                <span className="flex items-center gap-1 text-[1.5rem] font-semibold text-zinc-100">
                  Order #{displayOrderNum}
                </span>
              ))}
          </div>

          {(isLoading || reason) && (
            <div className="flex flex-col gap-1">
              <span className="text-[1.4rem] italic text-zinc-400">
                Reason:
              </span>
              {isLoading ? (
                <span className="h-[2.4rem] w-3/4 animate-pulse rounded bg-zinc-700" />
              ) : (
                <span className="text-[1.7rem] font-bold text-white">
                  {reason}
                </span>
              )}
            </div>
          )}

          <p className="whitespace-pre-line break-words text-[1.5rem] leading-[1.6] text-zinc-300">
            {bodyText}
          </p>
        </div>
      </div>
    </div>
  );
}
